import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col, Spinner } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { getDriverDetail } from "../../../../api/driver";
import { FileUpload, Input } from "../../../../components";
import dayjs from "dayjs";

const DriverVehicle = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [detail, setDetail] = useState({});
    const [loading, setLoading] = useState(true);
    const form = useForm();

    const getData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getDriverDetail(id);
            setDetail(response.vehicle);

            // Set form values for vehicle details
            if (response?.vehicle?.images[0]) {
                form.setValue("vehicleFront", response?.vehicle?.images[0]);
            }
            if (response?.vehicle?.images[1]) {
                form.setValue("vehicleBack", response?.vehicle?.images[1]);
            }
            if (response?.vehicle?.grant) {
                form.setValue("grant", response?.vehicle?.grant);
            }
            if (response?.vehicle?.roadTax) {
                form.setValue("roadTax", response?.vehicle?.roadTax);
            }
            if (response?.vehicle?.evp) {
                form.setValue("evp", response?.vehicle?.evp);
            }
        } catch (error) {
            console.error("Error fetching driver details:", error);
        } finally {
            setLoading(false);
        }
    }, [id, form]);

    useEffect(() => {
        getData();
    }, [getData]);

    const formatDate = (dateString) => {
        return dateString ? dayjs(dateString).format("DD/MM/YYYY") : t("NOT_AVAILABLE");
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t("LOADING")}</span>
                </Spinner>
            </div>
        );
    }

    return (
        <FormProvider {...form}>
            <Row>
                {/* Vehicle Photos */}
                <Col lg={4} md={6} className="mb-4">
                    <FileUpload
                        label={t("VEHICLE_FRONT")}
                        name="vehicleFront"
                        readOnly={true}
                        fileName={detail?.attachments[0]?.originalName || detail?.attachments[0]?.name}
                        uploadProps={{
                            className: "w-100"
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <FileUpload
                        label={t("VEHICLE_BACK")}
                        name="vehicleBack"
                        readOnly={true}
                        fileName={detail?.attachments[1]?.originalName || detail?.attachments[1]?.name}
                    />
                </Col>

                {/* Vehicle Information */}
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("REGISTRATION_NO")}
                        name="vehicleRegNo"
                        formControlProps={{
                            disabled: true,
                            value: detail?.vehicleRegNo || ""
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("BRAND")}
                        name="brand"
                        formControlProps={{
                            disabled: true,
                            value: detail?.vehicleBrand || ""
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("MODEL")}
                        name="model"
                        formControlProps={{
                            disabled: true,
                            value: detail?.vehicleBrand || ""
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("COLOR")}
                        name="color"
                        formControlProps={{
                            disabled: true,
                            value: detail?.vehicleColor || ""
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("SEAT_CAPACITY")}
                        name="seatCapacity"
                        formControlProps={{
                            disabled: true,
                            value: detail?.seatCapacity || ""
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("REGISTRATION_DATE")}
                        name="registrationDate"
                        formControlProps={{
                            disabled: true,
                            value: formatDate(detail?.registrationDate)
                        }}
                    />
                </Col>

                {/* Vehicle Documents */}
                <Col lg={4} md={6} className="mb-4">
                    <FileUpload
                        label={t("GRANT")}
                        name="grant"
                        readOnly={true}
                        fileName={detail?.grant?.originalName || detail?.grant?.name}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <FileUpload
                        label={t("ROAD_TAX")}
                        name="roadTax"
                        readOnly={true}
                        fileName={detail?.roadTax?.originalName || detail?.roadTax?.name}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("ROAD_TAX_EXPIRY_DATE")}
                        name="roadTaxExpiryDate"
                        formControlProps={{
                            disabled: true,
                            value: formatDate(detail?.roadTaxExpiryDate)
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <FileUpload
                        label={t("EVP")}
                        name="evp"
                        readOnly={true}
                        fileName={detail?.evp?.originalName || detail?.evp?.name}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("EVP_EXPIRY_DATE")}
                        name="evpExpiryDate"
                        formControlProps={{
                            disabled: true,
                            value: formatDate(detail?.evpExpiryDate)
                        }}
                    />
                </Col>
                <Col lg={4} md={6} className="mb-4">
                    <Input
                        label={t("INSURANCE_EXPIRY_DATE")}
                        name="insuranceExpiryDate"
                        formControlProps={{
                            disabled: true,
                            value: formatDate(detail?.insuranceExpiryDate)
                        }}
                    />
                </Col>
            </Row>
        </FormProvider>
    );
};

export default DriverVehicle;
